import React from 'react';
import { Alert, Spin, Tag, Tooltip, Typography } from 'antd';

import type {
  InspectAssessmentInstrument,
  InspectAssessmentPeriod,
  InspectAssessmentPeriodNews,
  InspectAssessmentPeriodPrices,
  InspectAssessmentPillarOnePeriod,
  InspectAssessmentUserAssignment,
} from '../../../../state/app/inspect_assessment/types';
import { inspectAssessmentPeriodPriceKey } from '../../../../state/app/inspect_assessment/actions';
import { assessmentPeriodMarketChanges } from './chartData';
import PeriodChangeTag from './PeriodChangeTag';

type PillarOneOutcome =
  | 'false-positive'
  | 'false-negative'
  | 'true-positive'
  | 'low-impact';

type PillarOnePair = {
  instrument: InspectAssessmentInstrument;
  ics: number;
  linked: boolean;
  outcome: PillarOneOutcome;
  latePeriodIndices: number[];
  periodReturn?: number;
};

type PillarOneNewsAudit = {
  id: number;
  headline: string;
  pairs: PillarOnePair[];
};

type PillarOnePeriodAudit = {
  period: InspectAssessmentPeriod;
  mappingPoints: number | null;
  userLinkCount: number | null;
  totalLinkCount: number | null;
  news: PillarOneNewsAudit[];
  loaded: boolean;
};

type Props = {
  active: boolean;
  assessmentId?: number;
  assignments: InspectAssessmentUserAssignment[];
  error?: string;
  fetching: boolean;
  hasSelectedAttempt: boolean;
  instruments: InspectAssessmentInstrument[];
  periodNewsByIndex: Record<number, InspectAssessmentPeriodNews>;
  periodPricesByKey: Record<string, InspectAssessmentPeriodPrices>;
  periods: InspectAssessmentPeriod[];
  pillarOneInstrumentCodes: string[];
  pillarOnePeriods: InspectAssessmentPillarOnePeriod[];
};

const MATERIAL_ICS_THRESHOLD = 0.25;

const OUTCOME_LABELS: Record<PillarOneOutcome, string> = {
  'false-positive': 'False Positive - wrong',
  'false-negative': 'False Negative - missed',
  'true-positive': 'True Positive - correct',
  'low-impact': 'Low Impact',
};

const assignmentKey = (newsId: number, instrumentId: number) =>
  `${newsId}:${instrumentId}`;

const formatMappingPoints = (mappingPoints: number | null) =>
  mappingPoints === null ? '—' : mappingPoints.toFixed(1);

const formatLinkCounts = (
  userLinkCount: number | null,
  totalLinkCount: number | null,
) =>
  userLinkCount === null || totalLinkCount === null
    ? '—/—'
    : `${userLinkCount}/${totalLinkCount}`;

const formatIcs = (ics: number) => `${ics > 0 ? '+' : ''}${ics.toFixed(2)}`;

const icsTone = (ics: number) =>
  Math.abs(ics) < MATERIAL_ICS_THRESHOLD
    ? 'neutral'
    : ics > 0
      ? 'positive'
      : 'negative';

const outcomeFor = (linked: boolean, material: boolean): PillarOneOutcome => {
  if (linked && material) return 'true-positive';
  if (linked) return 'false-positive';
  if (material) return 'false-negative';
  return 'low-impact';
};

const PillarOneScoreWorkspace: React.FC<Props> = ({
  active,
  assessmentId,
  assignments,
  error,
  fetching,
  hasSelectedAttempt,
  instruments,
  periodNewsByIndex,
  periodPricesByKey,
  periods,
  pillarOneInstrumentCodes,
  pillarOnePeriods,
}) => {
  const detailsRef = React.useRef<HTMLDivElement>(null);
  const sectionRefs = React.useRef(new Map<number, HTMLElement>());
  const scrollFrameRef = React.useRef<number>();
  const [activePeriodIndex, setActivePeriodIndex] = React.useState<
    number | undefined
  >(periods[0]?.index);

  const periodAudits = React.useMemo<PillarOnePeriodAudit[]>(() => {
    if (!active) return [];

    const mappingPointsByPeriod = new Map(
      pillarOnePeriods.map(row => [row.period_index, row.mapping_points]),
    );
    const periodIndexById = new Map<number, number>();
    periods.forEach(period => {
      if (period.assessment_period) {
        periodIndexById.set(period.assessment_period.id, period.index);
      }
    });

    const assignmentPeriodsByPair = new Map<string, Set<number>>();
    assignments.forEach(assignment => {
      if (assignment.news_id === null) return;
      const assignmentPeriodIndex = periodIndexById.get(assignment.period_id);
      if (assignmentPeriodIndex === undefined) return;
      const key = assignmentKey(assignment.news_id, assignment.instrument_id);
      const assignmentPeriods =
        assignmentPeriodsByPair.get(key) ?? new Set<number>();
      assignmentPeriods.add(assignmentPeriodIndex);
      assignmentPeriodsByPair.set(key, assignmentPeriods);
    });

    const eligibleCodes = new Set(pillarOneInstrumentCodes);
    const eligibleInstruments = instruments.filter(instrument =>
      eligibleCodes.has(instrument.code),
    );

    return periods.map(period => {
      const periodNews = periodNewsByIndex[period.index];
      const periodReturnsByInstrumentId = new Map(
        eligibleInstruments.map(instrument => {
          const periodPrices =
            assessmentId === undefined
              ? undefined
              : periodPricesByKey[
                  inspectAssessmentPeriodPriceKey(
                    assessmentId,
                    period.index,
                    instrument.id,
                  )
                ];
          return [
            instrument.id,
            assessmentPeriodMarketChanges(periodPrices).pricePercentage,
          ] as const;
        }),
      );
      const news = (periodNews?.items ?? []).flatMap(item => {
        if (
          item.mapping_status !== 'matched' ||
          !item.benchmark_news_score?.ics
        ) {
          return [];
        }

        const pairs = eligibleInstruments.flatMap(instrument => {
          const ics = item.benchmark_news_score?.ics?.[instrument.code];
          if (typeof ics !== 'number' || !Number.isFinite(ics)) return [];

          const assignmentPeriods = Array.from(
            assignmentPeriodsByPair.get(
              assignmentKey(item.id, instrument.id),
            ) ?? [],
          ).sort((left, right) => left - right);
          const linked = assignmentPeriods.includes(period.index);
          const material = Math.abs(ics) >= MATERIAL_ICS_THRESHOLD;
          return [
            {
              instrument,
              ics,
              linked,
              outcome: outcomeFor(linked, material),
              periodReturn: periodReturnsByInstrumentId.get(instrument.id),
              latePeriodIndices: assignmentPeriods.filter(
                assignmentPeriodIndex => assignmentPeriodIndex > period.index,
              ),
            },
          ];
        });

        return pairs.length > 0
          ? [{ id: item.id, headline: item.headline, pairs }]
          : [];
      });
      const pairs = news.flatMap(item => item.pairs);
      const countsAvailable =
        periodNews !== undefined && eligibleInstruments.length > 0;

      return {
        period,
        mappingPoints: mappingPointsByPeriod.get(period.index) ?? null,
        userLinkCount: countsAvailable
          ? pairs.filter(pair => pair.linked).length
          : null,
        totalLinkCount: countsAvailable
          ? pairs.filter(pair => pair.outcome !== 'low-impact').length
          : null,
        news,
        loaded: periodNews !== undefined,
      };
    });
  }, [
    active,
    assignments,
    assessmentId,
    instruments,
    periodNewsByIndex,
    periodPricesByKey,
    periods,
    pillarOneInstrumentCodes,
    pillarOnePeriods,
  ]);

  React.useEffect(() => {
    if (!active) return;
    if (
      activePeriodIndex === undefined ||
      !periodAudits.some(audit => audit.period.index === activePeriodIndex)
    ) {
      setActivePeriodIndex(periodAudits[0]?.period.index);
    }
  }, [active, activePeriodIndex, periodAudits]);

  React.useEffect(
    () => () => {
      if (scrollFrameRef.current !== undefined) {
        window.cancelAnimationFrame(scrollFrameRef.current);
      }
    },
    [],
  );

  const scrollToPeriod = (periodIndex: number) => {
    const details = detailsRef.current;
    const section = sectionRefs.current.get(periodIndex);
    if (!details || !section) return;
    setActivePeriodIndex(periodIndex);
    details.scrollTo({
      behavior: 'smooth',
      top: Math.max(0, section.offsetTop - 6),
    });
  };

  const syncActivePeriod = () => {
    if (scrollFrameRef.current !== undefined) {
      window.cancelAnimationFrame(scrollFrameRef.current);
    }
    scrollFrameRef.current = window.requestAnimationFrame(() => {
      const details = detailsRef.current;
      if (!details) return;
      const threshold = details.scrollTop + 16;
      let nextPeriodIndex = periodAudits[0]?.period.index;
      for (const audit of periodAudits) {
        const section = sectionRefs.current.get(audit.period.index);
        if (!section || section.offsetTop > threshold) break;
        nextPeriodIndex = audit.period.index;
      }
      setActivePeriodIndex(nextPeriodIndex);
    });
  };

  if (!active) return null;

  if (!hasSelectedAttempt) {
    return (
      <div className="assessment-inspect-assessment__pillar-one-status">
        {fetching ? (
          <Spin size="small" />
        ) : (
          <Typography.Text type="secondary">
            Select a user with an assessment session to inspect Pillar 1.
          </Typography.Text>
        )}
      </div>
    );
  }

  return (
    <div className="assessment-inspect-assessment__pillar-one-audit">
      <nav
        aria-label="Pillar 1 period index"
        className="assessment-inspect-assessment__pillar-one-period-index"
      >
        {periodAudits.map(audit => (
          <button
            aria-current={
              activePeriodIndex === audit.period.index ? 'location' : undefined
            }
            className={`assessment-inspect-assessment__pillar-one-period-link ${
              activePeriodIndex === audit.period.index
                ? 'assessment-inspect-assessment__pillar-one-period-link--active'
                : ''
            }`}
            key={audit.period.index}
            onClick={() => scrollToPeriod(audit.period.index)}
            onFocus={() => scrollToPeriod(audit.period.index)}
            onMouseEnter={() => scrollToPeriod(audit.period.index)}
            type="button"
          >
            <span>{audit.period.label}</span>
            <span>
              {formatMappingPoints(audit.mappingPoints)}
              <small>/40</small>
            </span>
          </button>
        ))}
      </nav>

      <div
        className="assessment-inspect-assessment__pillar-one-period-details"
        onScroll={syncActivePeriod}
        ref={detailsRef}
      >
        {pillarOnePeriods.length === 0 && !fetching && (
          <Alert
            message="Mapping points unavailable"
            description="This assessment attempt has no saved Pillar 1 period scores. No score fallback was applied."
            showIcon
            type="warning"
          />
        )}
        {pillarOneInstrumentCodes.length === 0 && !fetching && (
          <Alert
            message="Pillar 1 instrument universe unavailable"
            description="This assessment attempt has no stored playable Pillar 1 instruments. No resolver fallback was applied."
            showIcon
            type="warning"
          />
        )}
        {error && (
          <Alert
            message="Unable to load Pillar 1 news"
            description={error}
            showIcon
            type="error"
          />
        )}

        {periodAudits.map(audit => (
          <section
            className="assessment-inspect-assessment__pillar-one-period-section"
            key={audit.period.index}
            ref={node => {
              if (node) sectionRefs.current.set(audit.period.index, node);
              else sectionRefs.current.delete(audit.period.index);
            }}
          >
            <header className="assessment-inspect-assessment__pillar-one-period-header">
              <Typography.Text strong>{audit.period.label}</Typography.Text>
              <div>
                <Tag bordered={false} color="blue">
                  User linked news{' '}
                  {formatLinkCounts(audit.userLinkCount, audit.totalLinkCount)}
                </Tag>{' '}
                <Tag bordered={false} color="blue">
                  Mapping points {formatMappingPoints(audit.mappingPoints)}/40
                </Tag>
              </div>
            </header>

            {!audit.loaded ? (
              <div className="assessment-inspect-assessment__pillar-one-period-status">
                <Spin size="small" />
                <Typography.Text type="secondary">
                  Loading period news…
                </Typography.Text>
              </div>
            ) : audit.news.length === 0 ? (
              <Typography.Text type="secondary">
                No scored news–instrument pairs are available for this period.
              </Typography.Text>
            ) : (
              <div className="assessment-inspect-assessment__pillar-one-news-list">
                {audit.news.map(news => (
                  <article
                    className="assessment-inspect-assessment__pillar-one-news-item"
                    key={news.id}
                  >
                    <Typography.Text className="assessment-inspect-assessment__pillar-one-headline">
                      {news.headline}
                    </Typography.Text>
                    <div className="assessment-inspect-assessment__pillar-one-pairs">
                      {news.pairs.map(pair => (
                        <div
                          className="assessment-inspect-assessment__pillar-one-pair"
                          key={pair.instrument.id}
                        >
                          <Typography.Text className="assessment-inspect-assessment__pillar-one-instrument">
                            {pair.instrument.code}
                          </Typography.Text>
                          <Tag
                            bordered={false}
                            className={`assessment-inspect-assessment__pillar-one-outcome assessment-inspect-assessment__pillar-one-outcome--${pair.outcome}`}
                          >
                            {OUTCOME_LABELS[pair.outcome]}
                          </Tag>
                          {pair.latePeriodIndices.map(periodIndex => (
                            <Tooltip
                              key={periodIndex}
                              title={`This news appeared in ${audit.period.label}; the user linked it to ${pair.instrument.code} during P${periodIndex}. Pillar 1 only counts links saved against the news item's own assessment period.`}
                            >
                              <Tag
                                className="assessment-inspect-assessment__late-tag"
                                color="gold"
                                tabIndex={0}
                              >
                                Late (P{periodIndex})
                              </Tag>
                            </Tooltip>
                          ))}
                          <Typography.Text
                            className={`assessment-inspect-assessment__pillar-one-ics assessment-inspect-assessment__news-ics--${icsTone(pair.ics)}`}
                          >
                            ICS {formatIcs(pair.ics)}
                          </Typography.Text>
                          <PeriodChangeTag
                            change={{
                              key: 'return',
                              label: 'Return',
                              unit: '%',
                              value: pair.periodReturn,
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
};

export default PillarOneScoreWorkspace;
